import Link from "next/link";
import type { ComponentPropsWithoutRef, ReactNode } from "react";

type Variant =
  | "primary"
  | "secondary"
  | "ghost"
  | "inverse"
  | "outline-light";

type Size = "sm" | "md" | "lg";

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-lg font-medium tracking-tight transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-ring disabled:opacity-50 disabled:pointer-events-none";

const VARIANTS: Record<Variant, string> = {
  primary:
    "bg-brand text-white hover:bg-brand-strong shadow-[0_1px_2px_0_rgba(14,18,27,0.08),inset_0_-1px_0_0_rgba(0,0,0,0.08)]",
  secondary:
    "bg-white text-neutral-950 border border-neutral-200 hover:bg-neutral-50 shadow-[0_1px_2px_0_rgba(14,18,27,0.04)]",
  ghost: "text-neutral-950 hover:bg-neutral-100",
  inverse: "bg-white text-brand hover:bg-neutral-50",
  "outline-light":
    "border border-white/50 text-white hover:bg-white/10 backdrop-blur",
};

const SIZES: Record<Size, string> = {
  sm: "h-9 px-3.5 text-sm",
  md: "h-11 px-5 text-[15px]",
  lg: "h-[52px] px-6 text-base",
};

type Radiant = "white" | "brand";

type CommonProps = {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
  className?: string;
  /** Adds a hover-only rotating conic-gradient sweep around the border.
   *  "white" tints the sweep for filled brand backgrounds; "brand" tints
   *  it brand-blue for use on white/inverse backgrounds. */
  radiant?: Radiant;
};

type AnchorProps = CommonProps & {
  href: string;
} & Omit<ComponentPropsWithoutRef<typeof Link>, "href" | "className" | "children">;

type ButtonProps = CommonProps &
  Omit<ComponentPropsWithoutRef<"button">, "className" | "children"> & {
    href?: undefined;
  };

export function Button(props: AnchorProps | ButtonProps) {
  const {
    variant = "primary",
    size = "md",
    className = "",
    radiant,
    children,
    ...rest
  } = props;
  const radiantCls = radiant ? ` radiant-button tint-${radiant}` : "";
  const cls = `${BASE} ${VARIANTS[variant]} ${SIZES[size]}${radiantCls} ${className}`;

  if ("href" in props && props.href !== undefined) {
    const { href, ...linkRest } = rest as AnchorProps;
    return (
      <Link href={href} className={cls} {...linkRest}>
        {children}
      </Link>
    );
  }

  return (
    <button className={cls} {...(rest as ButtonProps)}>
      {children}
    </button>
  );
}
